import { motion } from "framer-motion";
import { ArrowUpRight, Building2, GraduationCap } from "lucide-react";
import HeroCanvas from "./HeroCanvas";
import { scrollToId } from "../lib/scroll";

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
});

const Hero = () => {
  return (
    <section className="relative blueprint-grid bg-[#F8FAFC] overflow-hidden">
      <div
        className="absolute inset-x-0 top-0 h-[520px] pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 0%, rgba(0,85,255,0.13) 0%, transparent 70%)" }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-16 lg:pt-44 lg:pb-24 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0055FF]/10 border border-[#0055FF]/20 mb-8"
            >
              <span className="w-2 h-2 bg-[#0055FF] animate-pulse-dot" />
              <span className="font-mono-x text-xs font-semibold uppercase tracking-[0.2em] text-[#0055FF]">
                Campus Incubation OS — by frantiger®
              </span>
            </motion.div>

            <h1
              data-testid="hero-kinetic-headline"
              className="font-display font-black tracking-tight leading-[1.02] text-[#0A0D14] text-4xl sm:text-5xl lg:text-6xl"
              style={{ fontWeight: 900 }}
            >
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" {...lineAnim(0)}>
                  TURN YOUR CAMPUS
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-1">
                <motion.span className="block text-[#0055FF]" {...lineAnim(1)}>
                  INTO A VENTURE
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span className="block" {...lineAnim(2)}>
                  ENGINE<span className="font-serif-accent text-[#0055FF]">.</span>
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
              className="mt-7 text-base sm:text-lg text-[#64748B] leading-relaxed max-w-xl"
            >
              frantiger labs is the incubation space for institutions without an
              in-house entrepreneurship cell — and for colleges that back their
              students' entrepreneurial ambitions. Ideation to funding to GTM,
              hand-held by a decade of frantiger legacy and{" "}
              <span className="text-[#0A0D14] font-semibold">100+ years of combined consulting experience</span>.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <button
                data-testid="hero-cta-institution-btn"
                onClick={() => scrollToId("apply")}
                className="group flex items-center justify-center gap-3 px-7 py-4 bg-[#0055FF] text-white font-mono-x text-sm uppercase tracking-[0.15em] hover:bg-[#0043CC] transition-colors"
              >
                <Building2 className="w-4 h-4" />
                Vibe Check for Colleges
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <button
                data-testid="hero-cta-student-btn"
                onClick={() => scrollToId("apply-student")}
                className="group flex items-center justify-center gap-3 px-7 py-4 border border-[#0A0D14] text-[#0A0D14] font-mono-x text-sm uppercase tracking-[0.15em] hover:bg-[#0A0D14] hover:text-white transition-colors"
              >
                <GraduationCap className="w-4 h-4" />
                Invite Us to Your Campus
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.25 }}
              className="mt-12 flex flex-wrap gap-x-8 gap-y-3 font-mono-x text-xs uppercase tracking-[0.15em] text-[#64748B]"
            >
              <span><span className="text-[#0055FF]">10+</span> yrs frantiger legacy</span>
              <span><span className="text-[#0055FF]">100+</span> yrs combined expertise</span>
              <span><span className="text-[#0055FF]">₹0</span> setup friction</span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative border border-[#0055FF]/25 bg-white/60 backdrop-blur-sm">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#0055FF]/20">
                <span className="font-mono-x text-[10px] uppercase tracking-[0.2em] text-[#0055FF]">
                  FIG.01 — INCUBATOR MATRIX
                </span>
                <span className="flex gap-1.5">
                  <span className="w-2 h-2 border border-[#0055FF]/40" />
                  <span className="w-2 h-2 bg-[#0055FF]" />
                </span>
              </div>
              <div className="h-[340px] sm:h-[420px]">
                <HeroCanvas />
              </div>
              <div className="flex items-center justify-between px-4 py-2.5 border-t border-[#0055FF]/20">
                <span className="font-mono-x text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
                  STATUS: ACCEPTING CAMPUSES
                </span>
                <span className="font-mono-x text-[10px] text-[#0055FF]">2026-27</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
